import { type Schedule, type ScheduleUpdate, fetcher } from "@repo/api";
import { formatDateToApiUTC, logError } from "@repo/utils";
import { endOfDay, startOfDay } from "date-fns";
import useSWR from "swr";

import { mutateWithStartKey } from "../mutateWithStartKey";

const formatScheduleDate = (date: Date | undefined, fieldName: string) => {
  if (!date) {
    return "";
  }

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    logError(new RangeError("Invalid time value"), [
      { tagName: "scheduleDate", tagValue: fieldName },
      { tagName: "scheduleDateValue", tagValue: String(date) },
    ]);
    return "";
  }

  try {
    return formatDateToApiUTC(date);
  } catch (error) {
    logError(error, [
      { tagName: "scheduleDate", tagValue: fieldName },
      { tagName: "scheduleDateValue", tagValue: String(date) },
    ]);
    return "";
  }
};

export const useSchedules = ({
  teamId,
  emailId,
  fromDate,
  toDate,
  enabled,
}: {
  teamId?: number;
  emailId?: string;
  fromDate?: Date;
  toDate?: Date;
  enabled?: boolean;
}) => {
  const fromDateStr = formatScheduleDate(
    fromDate ? startOfDay(fromDate) : undefined,
    "fromDate"
  );
  const toDateStr = formatScheduleDate(
    toDate ? endOfDay(toDate) : undefined,
    "toDate"
  );
  const encodedFromDateStr = encodeURIComponent(fromDateStr);
  const encodedToDateStr = encodeURIComponent(toDateStr);
  const encodedEmailId = emailId ? encodeURIComponent(emailId) : "";

  const getKey = () => {
    if (enabled === false) {
      return null;
    }

    if (teamId != null) {
      return `/schedules?teams=${teamId}&fromDate=${encodedFromDateStr}&toDate=${encodedToDateStr}`;
    }

    if (emailId) {
      return `/schedules?emailIds=${encodedEmailId}&fromDate=${encodedFromDateStr}&toDate=${encodedToDateStr}`;
    }

    return null;
  };

  return useSWR<Schedule[] | undefined>(
    getKey(),
    async (url) => {
      try {
        const res = await fetcher.core.get<Schedule[] | undefined>(url);
        return res;
      } catch {
        return undefined;
      }
    },
    {
      revalidateOnFocus: true,
    }
  );
};

export const updateSchedule = (id: string, newSchedule: ScheduleUpdate) => {
  return fetcher.core
    .update(`/schedules/${id}`, newSchedule)
    .then(async (res) => {
      mutateWithStartKey("/schedules");
      return await res.json();
    });
};

export const deleteSchedule = (id: string) => {
  return fetcher.core.remove(`/schedules/${id}`).then((res) => {
    mutateWithStartKey("/schedules");
    return res;
  });
};
